import React from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import { PaymentRecord } from './hooks/useActivationPaymentSettings';

interface PaymentRecordsTableProps {
    paymentRecords: PaymentRecord[];
    isLoading?: boolean;
}

export default function PaymentRecordsTable({
    paymentRecords,
    isLoading = false,
}: PaymentRecordsTableProps) {
    const formatCurrency = (cents: number) => {
        return `$${(cents / 100).toFixed(2)}`;
    };

    const formatDateTime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Payment Records</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-center py-8">
                        <div className="text-gray-500">
                            Loading payment records...
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (paymentRecords.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Payment Records</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8 text-gray-500">
                        No payments found for the selected date range
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Payment Records ({paymentRecords.length})</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Session ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Paid At
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Claimed At
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Redemption Code
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Payment Intent
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paymentRecords.map((payment) => (
                                <tr
                                    key={payment.id}
                                    className="hover:bg-gray-50"
                                >
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {payment.photoSessionId}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span className="font-semibold text-green-600">
                                            {formatCurrency(
                                                payment.costInCents,
                                            )}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {payment.paidAt
                                            ? formatDateTime(payment.paidAt)
                                            : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {payment.claimedAt
                                            ? formatDateTime(payment.claimedAt)
                                            : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                                            {payment.redemptionCode}
                                        </code>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {payment.paymentIntentId ? (
                                            <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                                                {payment.paymentIntentId.substring(
                                                    0,
                                                    20,
                                                )}
                                                ...
                                            </code>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {paymentRecords.length > 0 && (
                    <div className="mt-4 text-sm text-gray-500 text-center">
                        Showing {paymentRecords.length} payment
                        {paymentRecords.length !== 1 ? 's' : ''}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
